import type { Envelope, ViewerBridgeConfig, ViewerBridge } from '../types';
import { buildEnvelope, isEnvelope } from '../protocol';
import { uuidv4 } from '../utils/uuid';
import { readParentOrigin } from '../utils/origin';
import { normalizeLegacyToEnvelope } from './legacyCompat';

function readContextFromUrl(): { viewerType: string; schemaVersion: string; sessionId: string } {
  if (typeof window === 'undefined') return { viewerType: 'veranda', schemaVersion: '1.0.0', sessionId: uuidv4() };
  const hostname = String(window.location.hostname || '').toLowerCase();
  const hostLock =
    hostname.startsWith('dormer.') ? 'dormer'
      : hostname.startsWith('veranda.') ? 'veranda'
        : hostname.startsWith('windows.') ? 'windows'
          : null;

  const params = new URLSearchParams(window.location.search || '');
  const rawParam = params.get('viewerType') || params.get('viewer');
  const raw = String(rawParam || '').trim();
  // Accept the hostname prefix for any <type>.bry3d.com pattern
  const hostnamePrefix = hostname.split('.')[0] || '';
  const viewerType = hostLock || raw || hostnamePrefix || 'veranda';
  const schemaVersion = (params.get('schemaVersion') || '1.0.0').trim() || '1.0.0';
  const sessionId = (params.get('sessionId') || '').trim() || uuidv4();
  return { viewerType, schemaVersion, sessionId };
}

/**
 * Create a postMessage bridge for the iframe-side viewer.
 *
 * Listens for INIT, SET_OPTION, SET_OPTIONS, COMMIT from the parent page.
 * Normalizes legacy `configurator:v1` messages automatically.
 *
 * Call `bridge.start()` to send READY and begin listening.
 * Call `bridge.stop()` to tear down the listener.
 */
export function createViewerBridge(config: ViewerBridgeConfig = {}): ViewerBridge {
  const parentOrigin = readParentOrigin();
  const contextFromUrl = readContextFromUrl();

  const ctx = {
    sessionId: contextFromUrl.sessionId,
    viewerType: config.viewerType || contextFromUrl.viewerType,
    schemaVersion: config.schemaVersion || contextFromUrl.schemaVersion,
  };

  function post(type: string, payload: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    if (!window.parent || window.parent === window) return;
    try {
      const msg = buildEnvelope(type, payload, ctx);
      window.parent.postMessage(msg, parentOrigin);
    } catch {
      // ignore
    }
  }

  function isAllowed(ev: MessageEvent): boolean {
    if (parentOrigin !== '*' && ev.origin !== parentOrigin) return false;
    return true;
  }

  function handler(ev: MessageEvent): void {
    if (!ev || !isAllowed(ev)) return;
    const data = ev.data;
    if (!data) return;

    // Modern envelope or legacy normalization
    const msg: Envelope | null = isEnvelope(data)
      ? data as Envelope
      : normalizeLegacyToEnvelope(data, ctx);
    if (!msg) return;

    const type = msg.type;
    if (!type || typeof type !== 'string') return;

    if (type === 'INIT' && config.onInit) {
      config.onInit(msg.payload?.options as Record<string, unknown> || {});
      return;
    }

    if (type === 'SET_OPTION' && config.onSetOption) {
      config.onSetOption(
        msg.payload?.key as string,
        msg.payload?.value,
        msg.payload?.source as string | undefined,
      );
      return;
    }

    if (type === 'SET_OPTIONS' && config.onSetOptions) {
      config.onSetOptions(
        msg.payload?.patch as Record<string, unknown> || {},
        msg.payload?.source as string | undefined,
      );
      return;
    }

    if (type === 'COMMIT' && config.onCommit) {
      config.onCommit(msg.payload?.reason as string || 'unknown');
      return;
    }

    // Fallback: forward any unhandled message type
    if (config.onMessage) {
      config.onMessage(msg);
    }
  }

  function start(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('message', handler);
    post('READY', { viewerType: ctx.viewerType, schemaVersion: ctx.schemaVersion });
  }

  function stop(): void {
    if (typeof window === 'undefined') return;
    window.removeEventListener('message', handler);
  }

  function sendState(options: Record<string, unknown>): void {
    post('STATE', { options });
  }

  function sendError(code: string, message?: string): void {
    post('ERROR', { code, message: message || code });
  }

  return { parentOrigin, start, stop, sendState, sendError };
}
